import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, ExternalLink as ExternalIcon, FileSearch, X } from "lucide-react";
import type { IntelDocument, IntelMatchClass, IntelPivot, IntelView, WebResult } from "../../../shared/contracts.js";
import { api } from "../../api.js";
import { Badge } from "../../components/ui/Badge.js";
import { Spinner } from "../../components/ui/States.js";
import { useToast } from "../../components/ui/Toast.js";

const CLASS_LABEL: Record<IntelMatchClass, string> = {
  exacta: "Relevante para el caso",
  homonimo: "Coincidencia parcial",
  fuera_de_contexto: "Fuera de contexto",
};
const CLASS_TONE: Record<IntelMatchClass, "success" | "warning" | "neutral"> = {
  exacta: "success", homonimo: "warning", fuera_de_contexto: "neutral",
};
const PIVOT_LABEL: Record<IntelPivot["kind"], string> = {
  plate: "Patente", vin: "VIN", phone: "Teléfono", email: "Correo", rut: "RUT",
};

/**
 * Estado del análisis profundo de una búsqueda: se consulta cada 4 s mientras
 * queden trabajos pendientes (lo hace solo, sin que el analista haga nada).
 */
export function useIntel(searchId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["search-intel", searchId],
    queryFn: ({ signal }) => api.searches.intel(searchId!, signal),
    enabled: enabled && !!searchId,
    refetchInterval: (query) => ((query.state.data?.pending ?? 1) > 0 ? 4000 : false),
    retry: 1,
  });
}

/** Aviso arriba de los resultados mientras el motor sigue trabajando. */
export function IntelProgress({ v }: { v: IntelView | undefined }) {
  if (!v || v.pending === 0) return null;
  return (
    <div role="status" className="mb-3 flex items-center gap-2 rounded-lg border border-accent/30 bg-accent-soft px-3 py-2 text-[12.5px] text-accent-soft-fg">
      <Spinner className="size-3.5" />
      <span>Buscando más resultados: leyendo páginas y siguiendo pistas… ({v.pending} pendiente{v.pending === 1 ? "" : "s"})</span>
    </div>
  );
}

/** Páginas leídas completas que hablan del caso (pestaña "Resultados"). */
export function IntelResults({ v }: { v: IntelView | undefined }) {
  if (!v || (!v.enabled && v.documents.length === 0)) return null;
  const read = v.documents.filter((d) => d.status === "ok" && d.level === 1);
  const relevant = read.filter((d) => d.class !== "fuera_de_contexto");
  if (relevant.length === 0 && v.pending === 0 && read.length === 0) return null;

  return (
    <section aria-label="Análisis profundo" className="mt-3 rounded-xl border border-border bg-surface shadow-card">
      <header className="flex items-start gap-2 border-b border-border px-4 py-3">
        <FileSearch className="mt-0.5 size-4 text-accent" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-semibold text-fg">Análisis profundo</h3>
          <p className="text-[12px] text-muted">Páginas leídas completas y comparadas con los datos del caso.</p>
        </div>
      </header>
      <ul className="divide-y divide-border">
        {relevant.map((d) => <DocItem key={d.id} d={d} />)}
      </ul>
      {relevant.length === 0 && (
        <p className="px-4 py-3 text-[13px] text-muted">
          {v.pending > 0 ? "Leyendo las páginas de los resultados…" : "Ninguna página leída habla del caso."}
        </p>
      )}
      {read.length > relevant.length && (
        <p className="px-4 py-2 text-[12px] text-subtle">{read.length - relevant.length} página(s) leídas quedaron fuera de contexto.</p>
      )}
    </section>
  );
}

/** Cantidad de búsquedas recomendadas por revisar (para la pestaña). */
export function suggestedCount(v: IntelView | undefined): number {
  return v ? v.pivots.filter((p) => p.status === "suggested").length : 0;
}

/**
 * Pestaña "Búsquedas recomendadas": pistas nuevas encontradas en las páginas del
 * caso. Las del vehículo se buscaron solas; las demás se buscan solo si el analista
 * lo aprueba (queda registrado). Debajo, lo que trajo cada pista.
 */
export function IntelSuggestions({ searchId, v, results }: { searchId: string; v: IntelView | undefined; results: WebResult[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const act = useMutation({
    mutationFn: (a: { pivotId: string; action: "approve" | "dismiss" }) => api.searches.pivot(searchId, a.pivotId, a.action),
    onSuccess: (_d, a) => {
      if (a.action === "approve") toast.success("Pista aprobada: se buscará en internet y quedará registrada.");
      void qc.invalidateQueries({ queryKey: ["search-intel", searchId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!v || (!v.enabled && v.pivots.length === 0)) return null;

  const suggested = v.pivots.filter((p) => p.status === "suggested");
  const searched = v.pivots.filter((p) => p.status !== "suggested" && p.status !== "dismissed");
  const btn = "inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-[12px] font-medium text-muted hover:border-accent hover:text-fg disabled:opacity-50";

  return (
    <div className="divide-y divide-border">
      <section className="px-4 py-3">
        <h3 className="text-[13px] font-semibold text-fg">Pistas para revisar</h3>
        <p className="mb-2 text-[12px] text-subtle">Datos nuevos encontrados en páginas que hablan del caso. Se buscan solo si usted lo aprueba.</p>
        {suggested.length === 0 ? (
          <p className="text-[12.5px] text-muted">{v.pending > 0 ? "Buscando pistas…" : "No hay pistas por revisar."}</p>
        ) : (
          <ul className="space-y-2">
            {suggested.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2 text-[13px]">
                <Badge tone="outline">{PIVOT_LABEL[p.kind]}</Badge>
                <span className="font-mono font-medium text-fg">{p.value}</span>
                <span className="text-[12px] text-subtle">{p.sourceDomain ? `en ${p.sourceDomain}` : p.reason}</span>
                <span className="ml-auto flex gap-1">
                  <button type="button" className={btn} disabled={act.isPending} onClick={() => act.mutate({ pivotId: p.id, action: "approve" })}>
                    <Check className="size-3" aria-hidden="true" />Buscar
                  </button>
                  <button type="button" className={btn} disabled={act.isPending} aria-label={`Descartar ${p.value}`} onClick={() => act.mutate({ pivotId: p.id, action: "dismiss" })}>
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {searched.length > 0 && (
        <section className="px-4 py-3">
          <h3 className="mb-2 text-[13px] font-semibold text-fg">Pistas buscadas</h3>
          <ul className="space-y-3">
            {searched.map((p) => <SearchedPivot key={p.id} p={p} results={results.filter((r) => r.queryId.startsWith(`Y${p.id}.`))} docs={v.documents} />)}
          </ul>
        </section>
      )}
    </div>
  );
}

function SearchedPivot({ p, results, docs }: { p: IntelPivot; results: WebResult[]; docs: IntelDocument[] }) {
  const [open, setOpen] = useState(false);
  const scoreOf = (url: string): IntelDocument | undefined => docs.find((d) => d.url === url && d.status === "ok");
  return (
    <li>
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <Badge tone="outline">{PIVOT_LABEL[p.kind]}</Badge>
        <span className="font-mono font-medium text-fg">{p.value}</span>
        <span className="text-[12px] text-subtle">{p.mode === "auto" ? "buscada automáticamente" : "aprobada por el analista"}</span>
        <span className="ml-auto text-[12px] text-muted">
          {p.status === "queued" ? <span className="inline-flex items-center gap-1"><Spinner className="size-3" />buscando…</span>
            : p.status === "error" ? "no se pudo buscar" : `${results.length} resultado${results.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {results.length > 0 && (
        <>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="mt-1 inline-flex items-center gap-1 text-[12px] text-subtle hover:text-fg">
            Ver resultados <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {open && (
            <ul className="mt-1 space-y-1.5 border-l-2 border-border pl-3">
              {results.map((r) => {
                const d = scoreOf(r.url);
                return (
                  <li key={r.id} className="text-[12.5px]">
                    <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="font-medium text-accent hover:underline">{r.title}</a>
                    <span className="ml-1 text-subtle">{r.domain}</span>
                    {d?.class && <Badge tone={CLASS_TONE[d.class]} className="ml-1">{CLASS_LABEL[d.class]}</Badge>}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

/**
 * Identidad, separada de la relevancia: qué dato del caso aparece y con qué
 * respaldo. Un nombre igual no confirma identidad; una patente igual no prueba
 * relación con las personas del caso.
 */
function IdentityLines({ d }: { d: IntelDocument }) {
  const page = d.evidence.filter((e) => e.where === "pagina");
  const lines: string[] = [];
  for (const kind of ["plate", "vin", "rut", "phone", "email"] as const) {
    const e = page.find((x) => x.kind === kind);
    if (e) lines.push(`${({ plate: "Patente", vin: "VIN", rut: "RUT", phone: "Teléfono", email: "Correo" })[kind]} ${e.value} exacta, verificada en la página`);
  }
  const ocr = d.evidence.find((e) => e.where === "ocr" && !page.some((p) => p.kind === "plate" && p.value === e.value));
  if (ocr) lines.push(`Patente ${ocr.value} leída en una foto (OCR), pendiente de revisión`);
  const person = page.find((e) => e.kind === "person");
  if (person) lines.push(person.detail === "completo" ? `Nombre completo de ${person.value}: identidad pendiente` : `Nombre parcial de ${person.value}: puede ser otra persona`);
  if (!lines.length) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-[12px] text-muted">
      {lines.map((l) => <li key={l}>· {l}</li>)}
      {page.some((e) => e.kind === "plate") && !person && <li className="text-subtle">· La patente no prueba por sí sola relación con las personas del caso.</li>}
    </ul>
  );
}

function DocItem({ d }: { d: IntelDocument }) {
  const [open, setOpen] = useState(false);
  const cls = d.class ?? "fuera_de_contexto";
  return (
    <li className="px-4 py-3">
      <a href={d.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-start gap-1 text-[13.5px] font-medium text-accent hover:underline">
        <span className="line-clamp-2">{d.title || d.domain}</span>
        <ExternalIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      </a>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-subtle">
        <span className="font-medium text-muted">{d.domain}</span>
        <Badge tone={CLASS_TONE[cls]} title="Relevancia del contenido para el caso (no es una probabilidad)">{CLASS_LABEL[cls]}</Badge>
        {d.kind === "pdf" && <Badge tone="outline">PDF</Badge>}
        {d.imagesRead > 0 && <span>{d.imagesRead} imagen(es) leída(s)</span>}
      </div>
      <IdentityLines d={d} />
      {d.excerpt && <p className="mt-1 line-clamp-3 text-[12.5px] text-muted">{d.excerpt}</p>}
      {d.reasons.length > 0 && (
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          className="mt-1 inline-flex items-center gap-1 text-[12px] text-subtle hover:text-fg">
          Motivos <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      )}
      {open && (
        <ul className="mt-1 space-y-0.5 text-[12px] text-muted">
          {d.reasons.map((r, i) => (
            <li key={i}><span className={r.points >= 0 ? "text-success" : "text-danger"}>{r.points >= 0 ? "a favor:" : "en contra:"}</span> {r.detail}</li>
          ))}
        </ul>
      )}
    </li>
  );
}
